import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Briefcase, LogOut, Users, Wallet, Clock, CheckCircle2, TrendingUp, Link2, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthLabel = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(mo, 10) - 1]} ${y}`;
};

const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-700',
  repaid: 'bg-teal-100 text-teal-700',
  declined: 'bg-red-100 text-red-700',
  under_review: 'bg-purple-100 text-purple-700',
};

export default function AgentDashboard() {
  const navigate = useNavigate();
  const { logout, getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/agent/portal`, getAuthHeader());
        setData(res.data);
      } catch { /* handled by empty state */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => { logout(); navigate('/client/login'); };

  const referralLink = data?.agent?.referral_code
    ? `${window.location.origin}/apply?ref=${data.agent.referral_code}` : '';

  const copyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      toast.success('Referral link copied — share it with your clients');
    } catch {
      window.prompt('Copy your referral link:', referralLink);
    }
  };

  const downloadStatement = async (month) => {
    try {
      const res = await axios.get(`${API_URL}/agent/statement/${month}`, {
        ...getAuthHeader(), responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2k2day_agent_statement_${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download statement'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner"></div></div>;
  }

  const stats = data?.stats || {};
  const loans = (data?.loans || []).filter(l => monthFilter === 'all' || (l.date || '').startsWith(monthFilter));
  const months = data?.monthly || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agent-dashboard">
      {/* Header */}
      <header className="bg-[#0f2b4a] text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Briefcase className="w-5 h-5" /></div>
            <div>
              <h1 className="font-bold text-lg leading-tight">Agent Portal</h1>
              <p className="text-xs text-white/60">
                {data?.agent?.full_name}{data?.agent?.agency_name ? ` — ${data.agent.agency_name}` : ''} · {data?.agent?.commission_rate}% commission
              </p>
            </div>
          </div>
          <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10" onClick={handleLogout} data-testid="agent-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card data-testid="agent-stat-month-clients"><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><Users className="w-3.5 h-3.5" /> Clients This Month</div>
            <p className="text-2xl font-bold mt-1">{stats.month_clients || 0}</p>
          </CardContent></Card>
          <Card data-testid="agent-stat-month-commission"><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><TrendingUp className="w-3.5 h-3.5" /> Income This Month</div>
            <p className="text-2xl font-bold mt-1">R{(stats.month_commission || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card data-testid="agent-stat-pending"><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><Clock className="w-3.5 h-3.5" /> Pending Commission</div>
            <p className="text-2xl font-bold mt-1 text-amber-600">R{(stats.commission_pending || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card data-testid="agent-stat-earned"><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><CheckCircle2 className="w-3.5 h-3.5" /> Earned (Repaid)</div>
            <p className="text-2xl font-bold mt-1 text-emerald-600">R{(stats.commission_earned || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card data-testid="agent-stat-total"><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide"><Wallet className="w-3.5 h-3.5" /> Total Commission</div>
            <p className="text-2xl font-bold mt-1">R{(stats.commission_total || 0).toLocaleString()}</p>
            <p className="text-[11px] text-slate-400">{stats.total_loans || 0} loans · {stats.total_clients || 0} clients{stats.paid_out_total ? ` · R${stats.paid_out_total.toLocaleString()} paid out` : ''}</p>
          </CardContent></Card>
        </div>

        {/* Referral link */}
        {referralLink && (
          <Card data-testid="agent-referral-card">
            <CardContent className="pt-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-2 text-slate-600">
                <Link2 className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-medium">Your referral link</span>
              </div>
              <code className="flex-1 text-xs bg-slate-100 rounded-md px-3 py-2 truncate" data-testid="agent-referral-link">{referralLink}</code>
              <Button size="sm" variant="outline" onClick={copyReferral} data-testid="agent-copy-referral-btn">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
              <p className="text-[11px] text-slate-400 sm:max-w-[220px]">Clients who apply through this link are automatically linked to you for commission.</p>
            </CardContent>
          </Card>
        )}

        {/* Monthly breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Monthly Breakdown</CardTitle>
            <CardDescription>Commission is calculated on the approved capital only. Pending = loan approved; Earned = loan repaid.</CardDescription>
          </CardHeader>
          <CardContent>
            {months.length === 0 ? <p className="text-center text-slate-500 py-6">No loans linked to you yet.</p> : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="agent-monthly-table">
                  <thead>
                    <tr><th>Month</th><th>Clients</th><th>Loans</th><th>Capital (Approved)</th><th>Pending</th><th>Earned</th><th>Total Commission</th><th>Payout</th><th></th></tr>
                  </thead>
                  <tbody>
                    {months.map(m => (
                      <tr key={m.month}>
                        <td className="font-medium">{monthLabel(m.month)}</td>
                        <td className="tabular-nums">{m.clients}</td>
                        <td className="tabular-nums">{m.loans}</td>
                        <td className="tabular-nums">R{m.capital.toLocaleString()}</td>
                        <td className="tabular-nums text-amber-600">R{m.commission_pending.toLocaleString()}</td>
                        <td className="tabular-nums text-emerald-600">R{m.commission_earned.toLocaleString()}</td>
                        <td className="tabular-nums font-semibold">R{m.commission_total.toLocaleString()}</td>
                        <td>
                          {m.is_paid ? (
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-700" data-testid={`payout-badge-${m.month}`}>
                              Paid R{(m.payout_amount || 0).toLocaleString()}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-slate-100 text-slate-500">Unpaid</Badge>
                          )}
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <button className="text-xs text-indigo-600 hover:underline" data-testid={`agent-month-filter-${m.month}`}
                              onClick={() => setMonthFilter(monthFilter === m.month ? 'all' : m.month)}>
                              {monthFilter === m.month ? 'Show all' : 'View'}
                            </button>
                            <button className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5" data-testid={`agent-statement-btn-${m.month}`}
                              title="Download monthly statement PDF" onClick={() => downloadStatement(m.month)}>
                              <Download className="w-3.5 h-3.5" /> PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loans list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              My Clients &amp; Loans {monthFilter !== 'all' && <Badge variant="outline" className="ml-2">{monthLabel(monthFilter)}</Badge>}
            </CardTitle>
            <CardDescription>All loans linked to you by 2k2day.</CardDescription>
          </CardHeader>
          <CardContent>
            {loans.length === 0 ? <p className="text-center text-slate-500 py-6">No loans for this period.</p> : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="agent-loans-table">
                  <thead>
                    <tr><th>Ref</th><th>Client</th><th>Phone</th><th>Date</th><th>Applied</th><th>Approved</th><th>Status</th><th>Rate</th><th>Commission</th></tr>
                  </thead>
                  <tbody>
                    {loans.map(l => (
                      <tr key={l.id}>
                        <td className="font-mono text-xs">{l.reference}</td>
                        <td className="font-medium">{l.client_name}</td>
                        <td className="text-sm text-slate-500">{l.client_phone}</td>
                        <td className="text-xs text-slate-500">{l.date ? new Date(l.date).toLocaleDateString('en-ZA') : '—'}</td>
                        <td className="tabular-nums">R{(l.applied_amount || 0).toLocaleString()}</td>
                        <td className="tabular-nums font-medium">{l.approved_amount ? `R${l.approved_amount.toLocaleString()}` : '—'}</td>
                        <td><Badge variant="outline" className={STATUS_STYLES[l.status] || 'bg-blue-100 text-blue-700'}>{l.status}</Badge></td>
                        <td className="text-xs text-slate-500">{l.commission_rate ? `${l.commission_rate}%` : '—'}</td>
                        <td>
                          {l.commission_state === 'none' ? (
                            <span className="text-xs text-slate-400">awaiting approval</span>
                          ) : (
                            <span className={`tabular-nums font-semibold ${l.commission_state === 'earned' ? 'text-emerald-600' : 'text-amber-600'}`}>
                              R{l.commission.toLocaleString()}
                              <span className="block text-[10px] font-normal">{l.commission_state}</span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
